import { ENV } from "@/env";
import { useMutations } from "../services/queries";
import { Button, Checkbox, Icon, Modal, Typography, useCurrentDialog } from "@vintecc/components";
import { useState } from "react";

import { faScroll } from "@vintecc/pro-light-svg-icons";
import { useCommonTranslation } from "@/services/culture/common.translations";
import { useAdminTranslation } from "@/services/culture/admin.translations";

interface TermsAndPrivacyModalProps {
  needsTermsOfService: boolean;
  needsPrivacyPolicy: boolean;
  onComplete: () => void;
}

export const TermsAndPrivacyModal = ({ needsTermsOfService, needsPrivacyPolicy, onComplete }: TermsAndPrivacyModalProps) => {
  const { t: translateA } = useAdminTranslation();
  const { t: translateC } = useCommonTranslation();
  const { close } = useCurrentDialog();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { acceptTermsOfService, acceptPrivacyPolicy } = useMutations();

  const canSubmit = () => {
    if (needsTermsOfService && !acceptedTerms) return false;
    if (needsPrivacyPolicy && !acceptedPrivacy) return false;
    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const promises: Promise<unknown>[] = [];

      if (needsTermsOfService && acceptedTerms) {
        promises.push(acceptTermsOfService.mutateAsync());
      }

      if (needsPrivacyPolicy && acceptedPrivacy) {
        promises.push(acceptPrivacyPolicy.mutateAsync());
      }

      await Promise.all(promises);
      await onComplete();
      close();
    } catch (error) {
      console.error("Error accepting terms:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal className="w-full max-w-md p-8" testId="terms-and-privacy-modal">
      <section className="flex flex-col gap-6" role="form" aria-label={translateA("termsPrivacy.modal.title")}>
        <div className="flex flex-col gap-2">
          <Icon icon={faScroll} className="text-(--theme-default-color)" fontSize={36} />
          <Typography variant="h2" className="text-center">
            {translateA("termsPrivacy.modal.title")}
          </Typography>
          <Typography variant="p" className="text-center text-grey-600">
            {translateA("termsPrivacy.modal.description")}
          </Typography>
        </div>

        <div className="flex flex-col gap-4">
          {needsTermsOfService && ENV.TERMS_AND_CONDITIONS_URL && (
            <Checkbox
              name="checkbox-acceptedterms"
              checked={acceptedTerms}
              onChange={setAcceptedTerms}
              aria-label={translateA("termsPrivacy.modal.acceptTerms")}
              label={
                <span>
                  {translateA("termsPrivacy.modal.acceptTerms")}{" "}
                  <a
                    href={ENV.TERMS_AND_CONDITIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-(--theme-default-color) font-semibold hover:underline"
                  >
                    {translateA("termsPrivacy.termsOfUse")}
                  </a>
                </span>
              }
            />
          )}

          {needsPrivacyPolicy && ENV.PRIVACY_POLICY_URL && (
            <Checkbox
              name="checkbox-acceptedprivacy"
              checked={acceptedPrivacy}
              onChange={setAcceptedPrivacy}
              aria-label={translateA("termsPrivacy.modal.acceptPrivacy")}
              label={
                <span>
                  {translateA("termsPrivacy.modal.acceptPrivacy")}{" "}
                  <a
                    href={ENV.PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-(--theme-default-color) font-semibold hover:underline"
                  >
                    {translateA("termsPrivacy.privacyPolicy")}
                  </a>
                </span>
              }
            />
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={handleSubmit} disabled={!canSubmit() || isSubmitting}>
            {translateC("common.continue")}
          </Button>
        </div>
      </section>
    </Modal>
  );
};
